// SPDX-License-Identifier: AGPL-3.0-or-later

import {MessageReactions} from '@app/features/channel/components/MessageReactions';
import {TimestampWithTooltip} from '@app/features/channel/components/TimestampWithTooltip';
import type {Message} from '@app/features/messaging/models/Message';
import styles from '@app/features/theme/styles/Message.module.css';
import UserSettings from '@app/features/user/state/UserSettings';
import * as DateUtils from '@app/features/user/utils/dateFormatting';
import {useLingui} from '@lingui/react/macro';
import type {Icon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';
export const SystemMessage = observer(
	({
		icon: Icon,
		iconWeight,
		iconClassname,
		message,
		messageContent,
	}: {
		icon: Icon;
		iconWeight: 'bold' | 'fill';
		iconClassname?: string;
		message: Message;
		messageContent: React.ReactNode;
	}) => {
		const {i18n} = useLingui();
		const messageDisplayCompact = UserSettings.getMessageDisplayCompact();
		const formattedDate = useMemo(
			() =>
				messageDisplayCompact
					? DateUtils.getFormattedTime(message.timestamp)
					: DateUtils.getRelativeDateString(message.timestamp, i18n),
			[messageDisplayCompact, message.timestamp, i18n],
		);
		const showReactions = useMemo(
			() => UserSettings.getRenderReactions() && message.reactions.length > 0,
			[message.reactions.length],
		);
		if (messageDisplayCompact) {
			return (
				<div className={styles.systemMessageCompactContent}>
					<TimestampWithTooltip date={message.timestamp} className={styles.messageTimestampCompact}>
						{formattedDate}
					</TimestampWithTooltip>
					<div className={styles.systemMessageIconCompact}>
						<Icon weight={iconWeight} className={clsx(styles.systemMessageIconSvg, iconClassname)} />
					</div>
					<div className={styles.systemMessageContentWrapper}>
						<div className={styles.systemMessageContent} data-search-highlight-scope="message">
							{messageContent}
						</div>
						{showReactions && (
							<div className={styles.container}>
								<MessageReactions message={message} />
							</div>
						)}
					</div>
				</div>
			);
		}
		return (
			<>
				<div className={styles.messageGutterLeft} />
				<div className={styles.systemMessageIconWrapper}>
					<Icon weight={iconWeight} className={clsx(styles.systemMessageIconSvg, iconClassname)} />
				</div>
				<div className={styles.messageGutterRight} />
				<div className={styles.systemMessageContent}>
					<span data-search-highlight-scope="message">{messageContent}</span>{' '}
					<TimestampWithTooltip
						date={message.timestamp}
						className={clsx(styles.messageTimestamp, styles.systemMessageTimestamp)}
					>
						{formattedDate}
					</TimestampWithTooltip>
				</div>
				{showReactions && (
					<div className={styles.container}>
						<MessageReactions message={message} />
					</div>
				)}
			</>
		);
	},
);
